import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Paper, Typography, Box, Button, IconButton, Divider, Grid } from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import ShareIcon from '@mui/icons-material/Share';

import theme from '../../../theme/theme';
import { useNotification } from '../../../contexts/NotificationContext';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';
import SignatureDialog from "../anesthesiaForm/components/SignatureDialog";

import { usePreOpChecklist } from "./hooks/usePreOpChecklist";
import BasicInfoSection from "./screens/BasicInfoSection";
import ChecklistSection from "./screens/ChecklistSection";
import SpecialChecklistSection from "./screens/SpecialChecklistSection";
import BowelPrepAndFileSection from "./screens/BowelPrepAndFileSection";
import SignaturesSection from "./screens/SignaturesSection";

import axios from 'axios';
import { API_BASE_URL } from '../../../lib/constants';

const PreOperativeChecklist = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const {
    formData,
    loading,
    success,
    errors,
    signatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleAutocompleteChange,
    handleNameChange,
    handleCheckboxChange,
    handleSignatureOpen,
    handleSignatureClose,
    handleSignatureSave,
    handleBlur,
    handleSubmit
  } = usePreOpChecklist(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };

  const handleShareClick = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/generate_for_preop_checklist_api_share_token/${uhid_no}`);
      const token = res.data.token;
      const link = `${API_BASE_URL}/preOperativeChecklist/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease view/fill the pre-operative checklist:\n\n${link}`;

      await navigator.clipboard.writeText(link);
      showNotification("info", "Secure link copied to clipboard!");

      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      showNotification("error", "Failed to generate shareable link");
    }
  };

  if (loading && !formData.ip_no && !formData.name) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading checklist data...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            <Typography variant="h4" sx={{ color: '#008080', display: 'flex', alignItems: 'center', fontWeight: 600, letterSpacing: 0.5 }}>
              PRE-OPERATIVE CHECKLIST
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography variant="h6" sx={{ color: '#008080', marginLeft: 1, fontWeight: 500, letterSpacing: 0.3 }}>
                OTMS
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Button variant="outlined" onClick={handleBack} sx={{ marginLeft: 2 }}>
              Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button variant="contained" onClick={handleHomeNavigation} sx={{ marginRight: 1 }}>
                Home
              </Button>
              <Button variant="contained" onClick={() => goToVideoRecorder("PRE-OPERATIVE CHECKLIST", uhid_no)} sx={{ marginRight: 1 }}>
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>
          
          <br />
          
          <form onSubmit={handleSubmit}>
            <BasicInfoSection 
              formData={formData}
              handleChange={handleChange}
              handleNameChange={handleNameChange}
              handleAutocompleteChange={handleAutocompleteChange}
              handleBlur={handleBlur}
              errors={errors}
            />

            <Divider sx={{ my: 4 }} />

            <ChecklistSection 
              formData={formData} 
              handleCheckboxChange={handleCheckboxChange}
              handleAutocompleteChange={handleAutocompleteChange}
            />

            <Divider sx={{ my: 4 }} />

            <SpecialChecklistSection
              formData={formData}
              handleCheckboxChange={handleCheckboxChange}
              handleAutocompleteChange={handleAutocompleteChange}
            />

            <SignaturesSection 
              formData={formData}
              handleChange={handleChange}
              handleSignatureOpen={handleSignatureOpen}
            />

            <BowelPrepAndFileSection
              formData={formData}
              handleChange={handleChange}
              handleNameChange={handleNameChange}
              handleAutocompleteChange={handleAutocompleteChange}
              handleSignatureOpen={handleSignatureOpen}
            />

            <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.PREOPERATIVE_CHECKLIST}
                uhidNo={uhid_no}
                formData={formData}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large" 
                disabled={loading || Object.keys(errors).length > 0}
              >
                {loading ? "Submitting..." : (success ? "Update Checklist" : "Submit Checklist")}
              </Button>
            </Grid>
          </form>

          <SignatureDialog
            open={signatureDialogOpen}
            onClose={handleSignatureClose}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PreOperativeChecklist;
